/**
 * LiquidGlass —— Apple Liquid Glass 真折射材质原语（token 驱动，所有主题可用）
 * ------------------------------------------------------------------
 * 设计依据：Apple HIG Materials + WWDC25 Session 219《Meet Liquid Glass》；
 * Web 逆向：kube.io《Liquid Glass in the Browser: Refraction with CSS and SVG》
 *           LogRocket《How to create Liquid Glass effects with CSS and SVG》。
 *
 * 核心签名 = 折射（refraction / lensing），不是模糊：
 *   - 普通 glassmorphism = 半透明 + frosted blur + 软分层（iOS 7）
 *   - Liquid Glass      = glassmorphism ＋ 凸面折射（背景在玻璃边缘弯折）
 *                         ＋ 沿凸面轮廓的镜面 rim 高光（≈-60° 固定光源）
 *                         ＋ 边缘色散（chromatic aberration）
 *
 * v4 增强：
 *   - surface profile：convex（默认凸面）/ lip（凸外＋凹中，Apple Switch 标志）
 *   - 真 RGB 色散：feColorMatrix 拆 R/G/B 三通道 ＋ 各自 feDisplacementMap scale 微差 → 边缘彩虹 fringe
 *   - press 折射动态：按压时 displacementScale RAF 增大（kube「scale 可动画 fade 折射」）
 *   - displacement map LRU 缓存：相同尺寸跨实例共享，使多组件挂真折射性能可行
 *
 * 通用性：所有视觉参数读 CSS 变量（--glass-*），任意主题定义同名 token 即获材质。
 * 性能：map LRU 缓存 + ResizeObserver 节流；真折射仅 Chromium，Safari/FF 降级 blur+specular。
 */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/utils';

/* ──────────────────────────────────────────────────────────────────
 * 1. displacement + specular map 生成 —— 凸面 squircle / lip surface
 * ──────────────────────────────────────────────────────────────────
 * 两张图同一次遍历产出（共享 SDF/法线）：
 *  ① displacement（折射贴图）：R = 128 + dx·127，G = 128 + dy·127（[-1,1] → [0,255]）。
 *     - convex：bezel 带朝中心内法线（凸面 lensing，背景向内折射）。
 *     - lip：bezel 带凸（朝内）＋ 中心凹（朝外 zoom out，Apple Switch 中心放大签名）。
 *  ② specular（rim light 贴图）：强度 = bezel 衰减 × (径向外法线 · 固定光源点积)。
 *     朝光源半边亮、对侧暗（非均匀 rim）；RGB 色温渐变（朝光源冷白/掠射暖橙）。
 */

/** 光源方向（屏幕坐标，y 轴向下）。Apple Liquid Glass 固定光源 ≈ -60°，来自「左上偏上」。 */
const LIGHT_X = -0.5;
const LIGHT_Y = -0.866; // sin(60°)

export type LiquidSurface = 'convex' | 'lip';

/* LRU 缓存：相同 (尺寸,半径,bezel,surface,specular) 的 map 跨实例共享，
   使多组件挂真折射时不在每次 dims 变化都重算 Canvas（性能基建，v4）。 */
const _mapsCache = new Map<string, { disp: string; spec: string }>();
const CACHE_MAX = 16;

function generateLiquidMaps(
  width: number,
  height: number,
  radius: number,
  bezel: number,
  surface: LiquidSurface = 'convex',
  specular = true,
): { disp: string; spec: string } {
  const empty = { disp: '', spec: '' };
  if (typeof document === 'undefined') return empty;

  // 降采样上限：性能优先，feImage 拉伸填充（kube：圆形/squircle 中段可拉伸）。
  const MAX = 220;
  let w = Math.round(width);
  let h = Math.round(height);
  const longest = Math.max(w, h);
  if (longest > MAX) {
    const k = MAX / longest;
    w = Math.max(8, Math.round(width * k));
    h = Math.max(8, Math.round(height * k));
  }

  const key = `${w}x${h}|r${Math.round(radius)}|b${Math.round(bezel)}|${surface}|${specular ? 1 : 0}`;
  const cached = _mapsCache.get(key);
  if (cached) {
    _mapsCache.delete(key);
    _mapsCache.set(key, cached); // LRU：移到末尾（最近使用）
    return cached;
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return empty;

  const sx = width / w;
  const sy = height / h;

  const dispImg = ctx.createImageData(w, h);
  const dispData = dispImg.data;
  const specImg = specular ? ctx.createImageData(w, h) : null;
  const specData = specImg?.data;

  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(radius / sx, radius / sy, w / 2, h / 2);
  const halfW = w / 2 - r;
  const halfH = h / 2 - r;
  const bz = Math.max(2, Math.min(bezel / Math.max(sx, sy), Math.min(w, h) / 2));
  const halfMin = Math.min(halfW + r, halfH + r) || 1; // 内切半径，lip 中心凹的归一化基准

  // 圆角矩形 SDF（Inigo Quilez）：<0 内部，=0 边缘，>0 外部
  const sdRoundBox = (px: number, py: number) => {
    const qx = Math.abs(px) - halfW;
    const qy = Math.abs(py) - halfH;
    return (
      Math.min(Math.max(qx, qy), 0) + Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) - r
    );
  };

  const SPEC_GAIN = 1.35;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const px = x - cx;
      const py = y - cy;
      const d = sdRoundBox(px, py); // <0 inside
      const inside = -d; // 距内边缘的正距离
      const i = (y * w + x) * 4;

      // displacement 量：正=朝中心（凸 lensing），负=朝外（凹 zoom out）
      let dispMag = 0;
      let rimMag = 0; // specular rim 只在 bezel 带（凸边缘）

      if (inside >= 0 && inside <= bz) {
        // bezel 带：凸面 profile（边缘位移最大、内缘归零）
        const t = inside / bz; // 0=外缘, 1=内缘
        const u = 1 - t;
        const m = u * u * (3 - 2 * u); // smoothstep 反向
        dispMag = m;
        rimMag = m;
      } else if (surface === 'lip' && inside > bz) {
        // lip：中心凹（zoom out，背景放大），幅度向中心递增（Apple Switch 中心凸透镜签名）
        const t = Math.min(1, (inside - bz) / halfMin); // 0=bezel 内缘, 1=中心
        const concave = t * t * (3 - 2 * t); // smoothstep，中心最强
        dispMag = -concave * 0.55; // 朝外（凹），幅度克制避免越界撕裂
      }

      const len = Math.hypot(px, py) || 1;
      // displacement：朝中心（dispMag>0 凸）或朝外（dispMag<0 凹）
      const dx = (-px / len) * dispMag;
      const dy = (-py / len) * dispMag;
      dispData[i] = Math.round(128 + dx * 127);
      dispData[i + 1] = Math.round(128 + dy * 127);
      dispData[i + 2] = 128;
      dispData[i + 3] = 255;

      if (specData) {
        // specular：径向外法线与固定光源点积 → 朝光源半边 rim 亮、对侧暗
        const nx = px / len;
        const ny = py / len;
        const lightDot = nx * LIGHT_X + ny * LIGHT_Y; // [-1,1]
        const facing = Math.max(0, lightDot); // [0,1]，仅朝光源半边
        const specRaw = rimMag * facing * SPEC_GAIN; // [0, ~1.35]
        const s = Math.min(1, specRaw); // screen 限幅
        // 色温渐变（色散近似）：正对光源冷白、掠射带暖橙 → 边缘彩虹感
        const warm = 1 - facing;
        specData[i] = Math.round((245 - 35 * facing) * s); // facing↑→R↓(冷)
        specData[i + 1] = Math.round((232 - 12 * warm) * s);
        specData[i + 2] = Math.round((255 - 58 * warm) * s); // warm↑→B↓(暖橙)
        specData[i + 3] = 255;
      }
    }
  }

  ctx.putImageData(dispImg, 0, 0);
  const disp = canvas.toDataURL('image/png');
  let spec = '';
  if (specImg) {
    ctx.putImageData(specImg, 0, 0);
    spec = canvas.toDataURL('image/png');
  }
  const result = { disp, spec };

  // LRU 淘汰最久未用
  if (_mapsCache.size >= CACHE_MAX) {
    const firstKey = _mapsCache.keys().next().value;
    if (firstKey) _mapsCache.delete(firstKey);
  }
  _mapsCache.set(key, result);
  return result;
}

/* ──────────────────────────────────────────────────────────────────
 * 2. 特性检测：仅 Chromium 支持 SVG filter 作 backdrop-filter
 * ────────────────────────────────────────────────────────────────── */
let _svgBackdropSupported: boolean | null = null;
function supportsSvgBackdropFilter(): boolean {
  if (_svgBackdropSupported !== null) return _svgBackdropSupported;
  if (typeof window === 'undefined' || typeof CSS === 'undefined') {
    _svgBackdropSupported = false;
    return false;
  }
  const ua = window.navigator.userAgent;
  const isFirefox = /Firefox\//.test(ua);
  _svgBackdropSupported = /Chrome\//.test(ua) && !isFirefox;
  return _svgBackdropSupported;
}

/* ──────────────────────────────────────────────────────────────────
 * 3. 类型 & props
 * ────────────────────────────────────────────────────────────────── */
export type LiquidGlassVariant = 'regular' | 'clear';

export interface LiquidGlassProps {
  children?: ReactNode;
  className?: string;
  /** 形态变体：regular（默认强玻璃）/ clear（收敛档，媒体内容之上） */
  variant?: LiquidGlassVariant;
  /** 圆角半径（px）；默认读 CSS 变量 --squircle-r；胶囊用 999 */
  radius?: number;
  /** 折射带宽度（px）；默认 ≈ radius，控制边缘弯折范围 */
  bezel?: number;
  /** 折射强度（边缘最大像素位移）；默认 24，Apple 克制（≈ bezel 宽度量级，0=关闭折射） */
  displacementScale?: number;
  /** 表面 profile：convex（默认凸面）/ lip（凸外＋凹中，Apple Switch 中心放大签名） */
  surface?: LiquidSurface;
  /** 真 RGB 色散强度（百分比，如 4 = R/B 通道 ±4% scale 差）；默认 0（仅 spec 色温近似） */
  aberration?: number;
  /** 模糊（CSS 值，如 '18px'）；默认读 --glass-blur / --glass-blur-clear */
  blur?: string;
  /** 饱和度（CSS 值，如 '1.6'）；默认读 --glass-saturate */
  saturate?: string;
  /** 亮度（CSS 值，如 '1.04'）；默认读 --glass-brightness */
  brightness?: string;
  /** 是否渲染镜面 rim 高光（多层 inset box-shadow + spec map）；默认 true */
  specular?: boolean;
  /** 按压时折射动态增强（displacementScale RAF 增大 ~8px，kube「scale 可动画 fade 折射」）；默认 false */
  pressEffect?: boolean;
  /** 渲染标签；默认 div */
  as?: ElementType;
  /** 透传给根元素的内联 style */
  style?: CSSProperties;
}

/* ──────────────────────────────────────────────────────────────────
 * 4. SVG filter 定义（Chromium 折射管线）
 * ────────────────────────────────────────────────────────────────── */
function LiquidGlassFilter({
  id,
  map,
  specMap,
  scale,
  blur,
  specular,
  aberration,
}: {
  id: string;
  map: string;
  specMap: string;
  scale: number;
  blur: number;
  specular: boolean;
  aberration: number;
}) {
  if (!map) return null;
  const useSpec = specular && !!specMap;
  const useAberration = aberration > 0;
  const ab = aberration / 100; // 百分比 → 比例（如 4 → 0.04）
  // 三通道 scale：R 折射强、B 弱 → 边缘 RGB 错位（彩虹 fringe）
  const sR = scale * (1 + ab);
  const sG = scale;
  const sB = scale * (1 - ab);
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
    >
      <defs>
        <filter id={id} colorInterpolationFilters="sRGB" x="0" y="0" width="100%" height="100%">
          {/* 置换贴图（拉伸覆盖元素；feDisplacementMap 仍可采样边界外 backdrop 实现折射） */}
          <feImage href={map} x="0" y="0" width="100%" height="100%" preserveAspectRatio="none" result="disp" />
          {useAberration ? (
            <>
              {/* 真 RGB 色散：feColorMatrix 拆 R/G/B 三通道，各自 feDisplacementMap scale 微差 → 边缘彩虹 fringe */}
              <feColorMatrix in="SourceGraphic" type="matrix" values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0" result="rOnly" />
              <feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0" result="gOnly" />
              <feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0" result="bOnly" />
              <feDisplacementMap in="rOnly" in2="disp" scale={sR} xChannelSelector="R" yChannelSelector="G" result="rDisp" />
              <feDisplacementMap in="gOnly" in2="disp" scale={sG} xChannelSelector="R" yChannelSelector="G" result="gDisp" />
              <feDisplacementMap in="bOnly" in2="disp" scale={sB} xChannelSelector="R" yChannelSelector="G" result="bDisp" />
              {/* screen 合成还原彩色（screen 黑色不变，RGB 叠加还原）+ 边缘因错位出彩虹 fringe */}
              <feBlend in="rDisp" in2="gDisp" mode="screen" result="rg" />
              <feBlend in="rg" in2="bDisp" mode="screen" result="refracted" />
            </>
          ) : (
            <feDisplacementMap
              in="SourceGraphic"
              in2="disp"
              scale={scale}
              xChannelSelector="R"
              yChannelSelector="G"
              result="refracted"
            />
          )}
          {/* 轻微模糊收敛折射锯齿（Apple 亦有 Blur Level） */}
          <feGaussianBlur in="refracted" stdDeviation={Math.max(0, blur * 0.18)} result="soft" />
          {/* 法线驱动的 rim specular（色温渐变带色散感）：feBlend(screen) 叠加到折射之上 */}
          {useSpec && (
            <>
              <feImage href={specMap} x="0" y="0" width="100%" height="100%" preserveAspectRatio="none" result="spec" />
              <feBlend in="soft" in2="spec" mode="screen" result="highlighted" />
            </>
          )}
        </filter>
      </defs>
    </svg>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * 5. 主组件
 * ────────────────────────────────────────────────────────────────── */
export function LiquidGlass({
  children,
  className,
  variant = 'regular',
  radius,
  bezel,
  displacementScale = 24,
  surface = 'convex',
  aberration = 0,
  blur,
  saturate,
  brightness,
  specular = true,
  pressEffect = false,
  as,
  style,
}: LiquidGlassProps) {
  const Tag = (as ?? 'div') as ElementType;
  const rawId = useId();
  const id = `lg-${rawId.replace(/[:]/g, '')}`;
  const rootRef = useRef<HTMLElement | null>(null);

  const supported = useMemo(() => supportsSvgBackdropFilter(), []);
  const [dims, setDims] = useState<{ w: number; h: number; r: number } | null>(null);

  // ResizeObserver：尺寸变化时重算 displacement map（节流：仅尺寸真变化才更新）
  useEffect(() => {
    const el = rootRef.current;
    if (!el || !supported || displacementScale <= 0) return;
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const r =
          radius ??
          (parseFloat(getComputedStyle(el).borderRadius || '22') || 22);
        const w = Math.round(rect.width);
        const h = Math.round(rect.height);
        setDims((prev) =>
          prev && prev.w === w && prev.h === h && prev.r === r ? prev : { w, h, r },
        );
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [supported, displacementScale, radius]);

  // 生成 displacement + specular maps（dims 变化时；LRU 缓存跨实例共享）
  const maps = useMemo(() => {
    if (!supported || !dims || displacementScale <= 0) return { disp: '', spec: '' };
    const bz = bezel ?? Math.min(dims.r, 30);
    return generateLiquidMaps(dims.w, dims.h, dims.r, bz, surface, specular);
  }, [supported, dims, bezel, displacementScale, surface, specular]);

  // 从 CSS 变量读默认 blur/sat/bri（token 驱动，跨主题）；挂载后 rootRef 可用才读得到
  const [vars, setVars] = useState<{ blur: string; sat: string; bri: string } | null>(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const cs = getComputedStyle(el);
    const v = (name: string, fallback: string) => cs.getPropertyValue(name).trim() || fallback;
    setVars({
      blur: blur ?? v(`--glass-${variant === 'clear' ? 'blur-clear' : 'blur'}`, variant === 'clear' ? '8px' : '18px'),
      sat: saturate ?? v(`--glass-${variant === 'clear' ? 'saturate-clear' : 'saturate'}`, variant === 'clear' ? '1.3' : '1.6'),
      bri: brightness ?? v('--glass-brightness', '1.04'),
    });
  }, [blur, saturate, brightness, variant]);

  const filterId = `${id}-filter`;
  const useRefraction = supported && !!maps.disp && displacementScale > 0;
  const blurVal = vars?.blur ?? (variant === 'clear' ? '8px' : '18px');
  const satVal = vars?.sat ?? (variant === 'clear' ? '1.3' : '1.6');
  const briVal = vars?.bri ?? '1.04';

  // press 折射动态：按压时 displacementScale RAF 增大（kube「scale 可动画 fade 折射」）
  const [pressBoost, setPressBoost] = useState(0);
  useEffect(() => {
    if (!pressEffect || !useRefraction) return;
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = rootRef.current;
    if (!el) return;
    let raf = 0;
    let target = 0;
    let current = 0;
    let running = false;
    const tick = () => {
      current += (target - current) * 0.2;
      if (Math.abs(target - current) < 0.01) current = target;
      setPressBoost(current);
      if (current !== target) {
        raf = requestAnimationFrame(tick);
      } else {
        running = false;
      }
    };
    const start = (t: number) => {
      target = t;
      if (!running) {
        running = true;
        raf = requestAnimationFrame(tick);
      }
    };
    const down = () => start(1);
    const up = () => start(0);
    el.addEventListener('pointerdown', down);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('pointerdown', down);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [pressEffect, useRefraction]);

  const effectiveScale = displacementScale + Math.round(pressBoost * 8);

  // backdrop-filter：Chromium 真折射 url(#filter) saturate brightness；
  //                 其余浏览器降级 blur+saturate+brightness（无折射，CSS specular 仍在）
  const backdropFilter = useRefraction
    ? `url(#${filterId}) saturate(${satVal}) brightness(${briVal})`
    : `blur(${blurVal}) saturate(${satVal}) brightness(${briVal})`;

  const radiusStyle = radius != null ? `${radius}px` : 'var(--squircle-r, 22px)';

  return (
    <Tag
      ref={rootRef as never}
      data-liquid-glass=""
      data-variant={variant}
      data-surface={surface}
      data-refraction={useRefraction ? 'on' : 'off'}
      className={cn(
        'liquid-glass-root',
        variant === 'clear' && 'liquid-glass-root--clear',
        !specular && 'liquid-glass-root--no-spec',
        className,
      )}
      style={{ borderRadius: radiusStyle, ...style }}
    >
      {/* 折射 / 模糊层：absolute 铺满，承载 backdrop-filter（由 CSS .liquid-glass-fx 沉底） */}
      <span
        aria-hidden="true"
        className="liquid-glass-fx"
        style={{ borderRadius: 'inherit', backdropFilter, WebkitBackdropFilter: backdropFilter }}
      />
      {/* 内容层：直接渲染（CSS .liquid-glass-root > * 自动 position:relative;z-index:1 提升至 fx 之上） */}
      {children}
      {useRefraction && (
        <LiquidGlassFilter
          id={filterId}
          map={maps.disp}
          specMap={maps.spec}
          specular={specular}
          scale={effectiveScale}
          blur={parseFloat(blurVal)}
          aberration={aberration}
        />
      )}
    </Tag>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * 6. 全局共享 Liquid Glass filter（CSS backdrop-filter url() 复用）
 * ──────────────────────────────────────────────────────────────────
 * 固定 256² 凸面 squircle displacement+specular map，注入一次；dialog/sheet/
 * dropdown/select/toast 等浮层用 backdrop-filter: url(#liquid-glass-global) 获
 * 真折射，无需 per-element LiquidGlass 组件（固定 map 拉伸到元素；kube：squircle
 * 中段可拉伸；per-element 精确光照 web 不可得，此为合理近似 + 性能最优）。
 */
let _globalMaps: { disp: string; spec: string } | null = null;
function getGlobalMaps() {
  if (_globalMaps) return _globalMaps;
  if (typeof document === 'undefined') return null;
  _globalMaps = generateLiquidMaps(256, 256, 64, 32, 'convex', true);
  return _globalMaps;
}

export function LiquidGlassGlobalFilter({
  scale = 18,
  aberration = 2,
  blur = 3,
}: {
  scale?: number;
  aberration?: number;
  blur?: number;
}) {
  const supported = useMemo(() => supportsSvgBackdropFilter(), []);
  const maps = useMemo(() => getGlobalMaps(), []);
  // Chromium 注入 filter 时标记 :root，CSS 据此把浮层 backdrop-filter 切到 url() 真折射
  useEffect(() => {
    if (!supported || !maps) return;
    document.documentElement.setAttribute('data-lg-refraction', 'on');
    return () => document.documentElement.removeAttribute('data-lg-refraction');
  }, [supported, maps]);
  if (!supported || !maps) return null;
  return (
    <LiquidGlassFilter
      id="liquid-glass-global"
      map={maps.disp}
      specMap={maps.spec}
      specular
      scale={scale}
      blur={blur}
      aberration={aberration}
    />
  );
}

export default LiquidGlass;
